import type { ColDef, GridReadyEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useMemo, useRef } from 'react';
import type { LogProject } from '../services/timeEntryService';
import {
  agGridContainerStyles,
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { colors } from '../theme/colors';

interface LogProjectsGridProps {
  logProjectsData: LogProject[];
  isLoading?: boolean;
}

// Set filters start with nothing selected so users can search for a specific
// value directly instead of deselecting everything first. With no selection the
// filter is inactive, so all rows remain visible.
const gridDefaultColDef = {
  ...agGridDefaultColDef,
  filterParams: { defaultToNothingSelected: true },
};

export const LogProjectsGrid: React.FC<LogProjectsGridProps> = ({
  logProjectsData,
  isLoading = false,
}) => {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs = useMemo<ColDef<LogProject>[]>(
    () => [
      {
        headerName: 'Date',
        field: 'date',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        pinned: 'left',
        lockPosition: 'left',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Calendar Week',
        field: 'calendar_week',
        width: 130,
        minWidth: 100,
        maxWidth: 150,
        pinned: 'left',
        lockPosition: 'left',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
       
      },
      {
        headerName: 'Employee ID',
        field: 'employee_id',
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        lockPosition: 'left',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
       
      },
      {
        headerName: 'Employee Name',
        field: 'employee_name',
        flex: 1,
        minWidth: 180,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Project',
        field: 'project',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Order',
        field: 'order',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Task',
        field: 'task',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
       
      },
      {
        headerName: 'Work Type',
        field: 'work_type',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (!params.value) return '-';
          return params.value.toUpperCase();
        },
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 1,
        minWidth: 200,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          return (
            <div
              style={{
                padding: '0.25rem 0.5rem',
                color: colors.secondary.text,
                fontSize: '0.875rem',
              }}
            >
              {params.value || '-'}
            </div>
          );
        },
      },
      {
        headerName: 'Billable',
        field: 'billable',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        pinned: 'right',
        lockPosition: 'right',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellStyle: { overflow: 'hidden', textAlign: 'center' } as Record<string, string>,
      },
      {
        headerName: 'Amount',
        field: 'hours',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        pinned: 'right',
        lockPosition: 'right',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return '0';
          return typeof params.value === 'number' ? params.value.toFixed(2) : params.value;
        },
      },
    ],
    []
  );

  const onGridReady = (_params: GridReadyEvent) => {
    // Grid ready handler
  };

  return (
    <div style={{ height: 'calc(100dvh - 280px)' }}>
      <style>{agGridThemeStyles}</style>
      <AgGridReact
        ref={gridRef}
        rowData={logProjectsData}
        columnDefs={columnDefs}
        defaultColDef={gridDefaultColDef}
        gridOptions={{
          ...agGridDefaultOptions,
          theme: agGridTheme,
          onGridReady,
          loading: isLoading,
          suppressNoRowsOverlay: false,
          noRowsOverlayComponentParams: {
            message: 'No log projects data available',
          },
        }}
        animateRows={true}
        pagination={true}
        paginationPageSize={50}
        paginationPageSizeSelector={[25, 50, 100, 200]}
      />
    </div>
  );
};

